/**
 * Inventaris OPERASIONAL (Q170–Q172; D5 "bangun sebelum go-live"; ISIAN §1 A terpisah dari aset) — U33b.
 *
 * Tab Barang: daftar per cabang, tambah barang (kode INV-<STORE>-NNNN lahir di DB), detail + riwayat mutasi,
 *   catat mutasi (lokasi asal/tujuan, PIC kirim/terima, alasan, kondisi kirim & terima — Q171), hapuskan.
 * Tab Opname: buka opname PENTING (3 bulan) / LENGKAP (6 bulan) per store, periksa tiap baris, selesaikan
 *   (temuan diterapkan ke barang oleh RPC, Q172).
 * Kondisi & lokasi berubah HANYA lewat mutasi/opname (berhistori). Tombol tulis cermin backend:
 *   OWNER semua region; KEPALA_CABANG/ADMIN_GUDANG region sendiri. Penjaga sebenarnya 403 backend.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { toast } from "sonner";
import { Boxes, ArrowLeft, Plus, ClipboardCheck } from "lucide-react";
import { useStores } from "./bantuAset";
import { mintaTeks } from "../../lib/dialogAplikasi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import { BATAS_PILIHAN } from "../../lib/sumberPilihan";

const KONDISI_LABEL = { BAIK: "Baik", RUSAK_RINGAN: "Rusak ringan", RUSAK_BERAT: "Rusak berat", HILANG: "Hilang" };
const KONDISI_TONE = { BAIK: "success", RUSAK_RINGAN: "warning", RUSAK_BERAT: "danger", HILANG: "danger" };
const PERAN_TULIS = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"];
const pilih = "rounded-lg border border-input bg-background px-3 py-2 text-sm";
const saring = "h-9 rounded-md border border-input bg-background px-2 text-sm";
//: Cermin `URUT_SAH` / `URUT_SAH_OPNAME` di backend/services/inventaris_service.py.
const URUT_SAH = ["kode", "nama", "kondisi", "area", "created_at"];
const URUT_SAH_OPNAME = ["tanggal", "status", "jenis", "created_at"];
const LABEL_STATUS_BARANG = { AKTIF: "Aktif", DIHAPUSKAN: "Dihapuskan" };
const LABEL_STATUS_OPNAME = { DRAFT: "Berjalan", SELESAI: "Selesai" };

/** Sumber PemilihCari: karyawan aktif region (GET /inventaris/karyawan?q=&limit=) — cari di server. */
const cariPic = (regionId) => async (kata) => {
  const params = { limit: BATAS_PILIHAN, ...(kata ? { q: kata } : {}), ...(regionId ? { region_id: regionId } : {}) };
  return (await api.get("/inventaris/karyawan", { params })).data?.items || [];
};
const labelPic = (k) => (k ? `${k.full_name || ""}${k.employee_code ? ` (${k.employee_code})` : ""}` : "—");

function Kondisi({ v }) {
  return <StatusBadge tone={KONDISI_TONE[v] || "neutral"}>{KONDISI_LABEL[v] || v}</StatusBadge>;
}

export default function InventarisOperasional() {
  const { roleCode } = useAuth();
  const bolehTulis = PERAN_TULIS.includes(roleCode);
  const stores = useStores();
  const [tab, setTab] = useState("barang");
  const [meta, setMeta] = useState({ kategori: [], kondisi: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalOpname, setTotalOpname] = useState(0);
  const [baru, setBaru] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mutasi, setMutasi] = useState(null);
  const [opnames, setOpnames] = useState([]);
  const [opnameAktif, setOpnameAktif] = useState(null);
  const [opnameBaru, setOpnameBaru] = useState({ store_id: "", jenis: "PENTING" });

  const namaStore = (id) => (stores.find((s) => s.id === id) || {}).name || "—";

  // Pola daftar seragam (28 Sep 2026): barang dan riwayat opname halaman di SERVER (dulu berhenti diam-diam di
  // 1.000 barang / 200 opname). Dua daftar satu layar → opname memakai awalan alamat `op_`.
  // Saringan bawaan barang: status Aktif (chip bisa dihapus untuk melihat yang sudah dihapuskan).
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "kode", arah: "asc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: "AKTIF", tampil: (v) => LABEL_STATUS_BARANG[v] || v },
      store: { label: "Cabang", param: "store_id", tampil: (v) => namaStore(v) },
      kondisi: { label: "Kondisi", param: "kondisi", tampil: (v) => KONDISI_LABEL[v] || v },
    },
  });
  const dOpname = useDaftar(opnames, {
    server: { total: totalOpname, urutSah: URUT_SAH_OPNAME },
    urutAwal: { kunci: "tanggal", arah: "desc" },
    awalan: "op_",
    saringan: {
      status: { label: "Status", param: "status", tampil: (v) => LABEL_STATUS_OPNAME[v] || v },
      store: { label: "Cabang", param: "store_id", tampil: (v) => namaStore(v) },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const statusBarang = daftar.nilaiSaringan.status;
  // `status` SELALU dikirim: kosong = semua status (backend memakai "AKTIF" bila parameternya tidak ada).
  const bangunParams = useCallback((o = {}) => ({ ...paramSaringan, status: statusBarang || "",
    ...(cari && { q: cari }), limit, offset, sort, arah, ...o }), [paramSaringan, statusBarang, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/inventaris", { params: bangunParams({ offset: off, limit: n }) })).data, [bangunParams]);
  const po = dOpname.paramServer;
  const pso = dOpname.paramSaringan;
  const paramOpname = useCallback(() => ({ ...pso, ...(po.cari && { q: po.cari }), limit: po.limit, offset: po.offset, sort: po.sort, arah: po.arah }),
    [pso, po.cari, po.limit, po.offset, po.sort, po.arah]);

  const muatBarang = useCallback(async () => {
    try {
      const d = await api.get("/inventaris", { params: bangunParams() });
      setItems(d.data.items || []); setTotal(d.data.total ?? (d.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Inventaris gagal dimuat")); }
  }, [bangunParams]);
  const muatOpname = useCallback(async () => {
    try {
      const o = await api.get("/inventaris/opname", { params: paramOpname() });
      setOpnames(o.data.items || []); setTotalOpname(o.data.total ?? (o.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Riwayat opname gagal dimuat")); }
  }, [paramOpname]);
  useEffect(() => { muatBarang(); }, [muatBarang]);
  useEffect(() => { muatOpname(); }, [muatOpname]);
  useEffect(() => {
    api.get("/inventaris/meta").then(({ data }) => setMeta(data))
      .catch((e) => toast.error(apiErrorMessage(e, "Inventaris gagal dimuat")));
  }, []);
  const muat = useCallback(() => { muatBarang(); muatOpname(); }, [muatBarang, muatOpname]);
  const regionStore = (id) => (stores.find((s) => s.id === id) || {}).region_id || null;

  const simpanBaru = async () => {
    try {
      const { pic: _pic, ...isi } = baru;
      await api.post("/inventaris", { ...isi, harga_beli_idr: isi.harga_beli_idr ? Number(isi.harga_beli_idr) : null });
      toast.success("Inventaris ditambahkan."); setBaru(null); muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menambah inventaris")); }
  };
  const bukaDetail = async (id) => {
    try { const r = await api.get(`/inventaris/${id}`); setDetail(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Detail gagal dimuat")); }
  };
  const simpanMutasi = async () => {
    try {
      const { pengirim: _a, penerima: _b, ...isiMutasi } = mutasi;
      await api.post(`/inventaris/${detail.id}/mutasi`, isiMutasi);
      toast.success("Mutasi tercatat."); setMutasi(null); bukaDetail(detail.id); muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal mencatat mutasi")); }
  };
  const hapuskan = async () => {
    const alasan = await mintaTeks("Alasan menghapuskan barang ini:", { minPanjang: 5 });
    if (!alasan) return;
    try { await api.post(`/inventaris/${detail.id}/hapuskan`, { alasan }); toast.success("Barang dihapuskan."); setDetail(null); muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menghapuskan")); }
  };
  const bukaOpname = async () => {
    try { const r = await api.post("/inventaris/opname", opnameBaru); toast.success(`Opname dibuka (${r.data.jumlah_baris} barang).`); muat(); lihatOpname(r.data.id); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal membuka opname")); }
  };
  const lihatOpname = async (id) => {
    try { const r = await api.get(`/inventaris/opname/${id}`); setOpnameAktif(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Opname gagal dimuat")); }
  };
  const periksa = async (item, kondisi) => {
    try {
      await api.patch(`/inventaris/opname/${opnameAktif.id}/item/${item.id}`,
        { kondisi_ditemukan: kondisi, area_ditemukan: item.area_ditemukan ?? item.area_tercatat });
      lihatOpname(opnameAktif.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menyimpan pemeriksaan")); }
  };
  const selesai = async () => {
    try { const r = await api.post(`/inventaris/opname/${opnameAktif.id}/selesai`); toast.success(`Opname selesai — ${r.data.diterapkan} temuan diterapkan.`); lihatOpname(opnameAktif.id); muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menyelesaikan opname")); }
  };

  return (
    <div className="space-y-6" data-testid="inventaris-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/aset" className="text-xs text-muted-foreground inline-flex items-center gap-1"><ArrowLeft size={12} /> Daftar Aset</Link>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Boxes className="h-6 w-6" /> Inventaris Operasional</h1>
          <p className="text-sm text-muted-foreground mt-1">Rak, meja, alat kecil, perlengkapan gudang/cabang — lokasi, kondisi, mutasi, dan opname.</p>
          {roleCode === "OWNER" && (
            <Link to="/master/alasan/kategori-inventaris" className="text-xs underline" data-testid="inv-ke-kategori">Kelola kategori</Link>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={tab === "barang" ? "default" : "outline"} onClick={() => setTab("barang")} data-testid="inv-tab-barang">Barang</Button>
          <Button size="sm" variant={tab === "opname" ? "default" : "outline"} onClick={() => setTab("opname")} data-testid="inv-tab-opname">Opname</Button>
        </div>
      </div>

      {tab === "barang" && (
        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {bolehTulis && (
              <Button size="sm" onClick={() => setBaru({ nama: "", kategori_code: "", store_id: "", area: "", kondisi: "BAIK", penting: false })}
                      data-testid="inv-baru-btn"><Plus size={14} className="mr-1" /> Barang baru</Button>
            )}
          </div>
          {baru && (
            <div className="grid sm:grid-cols-3 gap-2 rounded-md border border-border p-3" data-testid="inv-form-baru">
              <Input placeholder="Nama barang" value={baru.nama} onChange={(e) => setBaru({ ...baru, nama: e.target.value })} />
              <select className={pilih} value={baru.kategori_code} onChange={(e) => setBaru({ ...baru, kategori_code: e.target.value })}>
                <option value="">Kategori…</option>
                {meta.kategori.filter((k) => k.is_active).map((k) => <option key={k.code} value={k.code}>{k.label_id}</option>)}
              </select>
              <select className={pilih} value={baru.store_id} onChange={(e) => setBaru({ ...baru, store_id: e.target.value, pic: null, pic_employee_id: null })}>
                <option value="">Cabang…</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input placeholder="Area (ruang/rak/lantai)" value={baru.area} onChange={(e) => setBaru({ ...baru, area: e.target.value })} />
              <select className={pilih} value={baru.kondisi} onChange={(e) => setBaru({ ...baru, kondisi: e.target.value })}>
                {meta.kondisi.map((k) => <option key={k} value={k}>{KONDISI_LABEL[k]}</option>)}
              </select>
              <PemilihCari value={baru.pic || null} bolehKosong tampilAwal disabled={!baru.store_id}
                           onChange={(k) => setBaru({ ...baru, pic: k, pic_employee_id: k?.id || null })}
                           cari={cariPic(regionStore(baru.store_id))} label={labelPic}
                           placeholder={baru.store_id ? "PIC (opsional)…" : "Pilih cabang dulu untuk PIC"}
                           placeholderCari="Ketik nama atau kode karyawan…" testId="inv-baru-pic" />
              <Input type="date" value={baru.tanggal_beli || ""} onChange={(e) => setBaru({ ...baru, tanggal_beli: e.target.value || null })} />
              <Input placeholder="Harga beli (Rp, info)" inputMode="numeric" value={baru.harga_beli_idr || ""} onChange={(e) => setBaru({ ...baru, harga_beli_idr: e.target.value.replace(/[^0-9]/g, "") })} />
              <Input placeholder="Vendor" value={baru.vendor || ""} onChange={(e) => setBaru({ ...baru, vendor: e.target.value })} />
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={baru.penting} onChange={(e) => setBaru({ ...baru, penting: e.target.checked })} /> Penting (opname 3 bulan)</label>
              <div className="flex gap-2 sm:col-span-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setBaru(null)}>Batal</Button>
                <Button size="sm" disabled={!baru.nama || !baru.kategori_code || !baru.store_id} onClick={simpanBaru} data-testid="inv-simpan-baru">Simpan</Button>
              </div>
            </div>
          )}
          <PalangDaftar daftar={daftar} placeholder="Cari kode / nama / area…" testId="inv-palang"
                        ekspor={{ nama: "inventaris", ambilHalaman, kolom: [
                          { kunci: "kode", judul: "Kode" }, { kunci: "nama", judul: "Nama" },
                          { kunci: "store", judul: "Cabang", ambil: (b) => namaStore(b.store_id) }, { kunci: "area", judul: "Area" },
                          { kunci: "kondisi", judul: "Kondisi", ambil: (b) => KONDISI_LABEL[b.kondisi] || b.kondisi },
                          { kunci: "status", judul: "Status", ambil: (b) => LABEL_STATUS_BARANG[b.status] || b.status }] }}>
            <select className={saring} value={daftar.nilaiSaringan.store} onChange={(e) => daftar.setSaringan("store", e.target.value)}
                    aria-label="Saring cabang" data-testid="inv-filter-store">
              <option value="">Semua cabang</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className={saring} value={daftar.nilaiSaringan.kondisi} onChange={(e) => daftar.setSaringan("kondisi", e.target.value)}
                    aria-label="Saring kondisi" data-testid="inv-filter-kondisi">
              <option value="">Semua kondisi</option>
              {Object.entries(KONDISI_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className={saring} value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                    aria-label="Saring status" data-testid="inv-filter-status">
              <option value="">Semua status</option>
              {Object.entries(LABEL_STATUS_BARANG).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </PalangDaftar>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground">
                <ThUrut daftar={daftar} kunci="kode" className="py-1 pr-2">Kode</ThUrut>
                <ThUrut daftar={daftar} kunci="nama" className="py-1 pr-2">Nama</ThUrut>
                <ThUrut daftar={daftar} kunci="area" className="py-1 pr-2">Cabang · area</ThUrut>
                <ThUrut daftar={daftar} kunci="kondisi" className="py-1 pr-2">Kondisi</ThUrut><th />
              </tr></thead>
              <tbody>
                {daftar.tampil.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`inv-baris-${b.kode}`}>
                    <td className="py-1 pr-2 font-mono text-xs">{b.kode}</td>
                    <td className="py-1 pr-2">{b.nama}{b.penting && <span className="ml-1 text-xs text-muted-foreground">★</span>}</td>
                    <td className="py-1 pr-2">{namaStore(b.store_id)}{b.area ? ` · ${b.area}` : ""}</td>
                    <td className="py-1 pr-2"><Kondisi v={b.kondisi} /></td>
                    <td className="py-1 text-right"><Button size="sm" variant="ghost" onClick={() => bukaDetail(b.id)}>Detail</Button></td>
                  </tr>
                ))}
                {daftar.total === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">
                  {daftar.cari.trim() || daftar.saringanAktif.some((x) => !x.bawaan) ? "Tidak ada yang cocok." : "Belum ada inventaris."}</td></tr>}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="inv-paginasi" />
          </div>
        </Card>
      )}

      {tab === "barang" && detail && (
        <Card className="p-4 space-y-3" data-testid="inv-detail">
          <div className="flex justify-between flex-wrap gap-2">
            <div><div className="font-medium">{detail.kode} — {detail.nama}</div>
              <div className="text-sm text-muted-foreground">{namaStore(detail.store_id)}{detail.area ? ` · ${detail.area}` : ""} · <Kondisi v={detail.kondisi} /></div></div>
            {bolehTulis && detail.status === "AKTIF" && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setMutasi({ store_tujuan_id: detail.store_id, area_tujuan: "", alasan: "", kondisi_kirim: detail.kondisi, kondisi_terima: detail.kondisi })} data-testid="inv-mutasi-btn">Catat mutasi</Button>
                <Button size="sm" variant="outline" onClick={hapuskan}>Hapuskan</Button>
              </div>
            )}
          </div>
          {mutasi && (
            <div className="grid sm:grid-cols-3 gap-2 rounded-md border border-border p-3" data-testid="inv-form-mutasi">
              <select className={pilih} value={mutasi.store_tujuan_id} onChange={(e) => setMutasi({ ...mutasi, store_tujuan_id: e.target.value })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input placeholder="Area tujuan" value={mutasi.area_tujuan} onChange={(e) => setMutasi({ ...mutasi, area_tujuan: e.target.value })} />
              <Input placeholder="Alasan (min. 5 karakter)" value={mutasi.alasan} onChange={(e) => setMutasi({ ...mutasi, alasan: e.target.value })} />
              <PemilihCari value={mutasi.pengirim || null} bolehKosong tampilAwal
                           onChange={(k) => setMutasi({ ...mutasi, pengirim: k, pic_pengirim_id: k?.id || null })}
                           cari={cariPic(detail.region_id)} label={labelPic} placeholder="PIC pengirim…"
                           placeholderCari="Ketik nama atau kode karyawan…" testId="inv-mutasi-pengirim" />
              <PemilihCari value={mutasi.penerima || null} bolehKosong tampilAwal
                           onChange={(k) => setMutasi({ ...mutasi, penerima: k, pic_penerima_id: k?.id || null })}
                           cari={cariPic(detail.region_id)} label={labelPic} placeholder="PIC penerima…"
                           placeholderCari="Ketik nama atau kode karyawan…" testId="inv-mutasi-penerima" />
              <div className="flex gap-2">
                <select className={pilih} value={mutasi.kondisi_kirim} onChange={(e) => setMutasi({ ...mutasi, kondisi_kirim: e.target.value })} title="Kondisi saat kirim">
                  {meta.kondisi.map((k) => <option key={k} value={k}>Kirim: {KONDISI_LABEL[k]}</option>)}
                </select>
                <select className={pilih} value={mutasi.kondisi_terima} onChange={(e) => setMutasi({ ...mutasi, kondisi_terima: e.target.value })} title="Kondisi saat terima">
                  {meta.kondisi.map((k) => <option key={k} value={k}>Terima: {KONDISI_LABEL[k]}</option>)}
                </select>
              </div>
              <div className="flex gap-2 sm:col-span-3 justify-end">
                <Button size="sm" variant="outline" onClick={() => setMutasi(null)}>Batal</Button>
                <Button size="sm" disabled={(mutasi.alasan || "").trim().length < 5} onClick={simpanMutasi} data-testid="inv-simpan-mutasi">Simpan mutasi</Button>
              </div>
            </div>
          )}
          <div>
            <div className="text-sm font-medium mb-1">Riwayat mutasi</div>
            {(detail.mutasi || []).map((m) => (
              <div key={m.id} className="text-xs border-t border-border py-1">
                {m.tanggal}: {namaStore(m.store_asal_id)}{m.area_asal ? ` · ${m.area_asal}` : ""} → {namaStore(m.store_tujuan_id)}{m.area_tujuan ? ` · ${m.area_tujuan}` : ""} · {KONDISI_LABEL[m.kondisi_kirim]} → {KONDISI_LABEL[m.kondisi_terima]} · {m.alasan}
              </div>
            ))}
            {(detail.mutasi || []).length === 0 && <div className="text-xs text-muted-foreground">Belum ada mutasi.</div>}
          </div>
        </Card>
      )}

      {tab === "opname" && (
        <Card className="p-4 space-y-3">
          {bolehTulis && (
            <div className="flex flex-wrap gap-2 items-center" data-testid="inv-opname-baru">
              <select className={pilih} value={opnameBaru.store_id} onChange={(e) => setOpnameBaru({ ...opnameBaru, store_id: e.target.value })}>
                <option value="">Cabang…</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select className={pilih} value={opnameBaru.jenis} onChange={(e) => setOpnameBaru({ ...opnameBaru, jenis: e.target.value })}>
                <option value="PENTING">Barang penting (tiap 3 bulan)</option>
                <option value="LENGKAP">Lengkap (tiap 6 bulan)</option>
              </select>
              <Button size="sm" disabled={!opnameBaru.store_id} onClick={bukaOpname}><ClipboardCheck size={14} className="mr-1" /> Buka opname</Button>
            </div>
          )}
          <PalangDaftar daftar={dOpname} placeholder="Cari nama cabang…" testId="inv-opname-palang">
            <select className={saring} value={dOpname.nilaiSaringan.store} onChange={(e) => dOpname.setSaringan("store", e.target.value)}
                    aria-label="Saring cabang" data-testid="inv-opname-filter-store">
              <option value="">Semua cabang</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className={saring} value={dOpname.nilaiSaringan.status} onChange={(e) => dOpname.setSaringan("status", e.target.value)}
                    aria-label="Saring status" data-testid="inv-opname-filter-status">
              <option value="">Semua status</option>
              {Object.entries(LABEL_STATUS_OPNAME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </PalangDaftar>
          <div className="space-y-1">
            {dOpname.tampil.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm border-t border-border py-1">
                <span>{o.tanggal} · {namaStore(o.store_id)} · {o.jenis === "PENTING" ? "Barang penting" : "Lengkap"}</span>
                <span className="flex items-center gap-2"><StatusBadge tone={o.status === "SELESAI" ? "success" : "warning"}>{o.status === "SELESAI" ? "Selesai" : "Berjalan"}</StatusBadge>
                  <Button size="sm" variant="ghost" onClick={() => lihatOpname(o.id)}>Buka</Button></span>
              </div>
            ))}
            {dOpname.total === 0 && <div className="text-sm text-muted-foreground">
              {dOpname.saringanAktif.length ? "Tidak ada yang cocok." : "Belum ada opname."}</div>}
            <Paginasi daftar={dOpname} testId="inv-opname-paginasi" />
          </div>
          {opnameAktif && (
            <div className="rounded-md border border-border p-3 space-y-2" data-testid="inv-opname-aktif">
              <div className="flex justify-between"><div className="font-medium text-sm">Opname {opnameAktif.tanggal} · {namaStore(opnameAktif.store_id)}</div>
                {bolehTulis && opnameAktif.status === "DRAFT" && <Button size="sm" onClick={selesai} data-testid="inv-opname-selesai">Selesaikan</Button>}</div>
              <table className="w-full text-sm"><tbody>
                {(opnameAktif.item || []).map((i) => (
                  <tr key={i.id} className="border-t border-border">
                    <td className="py-1 pr-2 font-mono text-xs">{i.kode}</td><td className="py-1 pr-2">{i.nama}</td>
                    <td className="py-1 pr-2 text-xs">Tercatat: {KONDISI_LABEL[i.kondisi_tercatat]}{i.area_tercatat ? ` · ${i.area_tercatat}` : ""}</td>
                    <td className="py-1">
                      {opnameAktif.status === "DRAFT" && bolehTulis ? (
                        <select className={pilih} value={i.kondisi_ditemukan || ""} onChange={(e) => periksa(i, e.target.value)} data-testid={`inv-periksa-${i.kode}`}>
                          <option value="">Ditemukan…</option>{meta.kondisi.map((k) => <option key={k} value={k}>{KONDISI_LABEL[k]}</option>)}
                        </select>
                      ) : (i.kondisi_ditemukan ? <Kondisi v={i.kondisi_ditemukan} /> : "—")}
                      {i.selisih && <span className="ml-1 text-xs text-destructive">selisih</span>}
                    </td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
